// ============================================================================
//  NhisMedicinesList — the published list, and what kind of facility this is
// ============================================================================
//
//  ASKED FOR: "This medication will keep changing and keep changing. I want us
//   to build it under the administrative one, where we can upload those ones and
//   save inside a new table. And find a way to also set the level, still on the
//   administrative side, so that we can cross check it when the medications are
//   being prescribed at a claim site. Only the claim site."
//
//  Then: "using definition make it more actionable."
//
//  ── WHY THE DEFINITIONS ARE ON THE SCREEN ──────────────────────────────────
//
//  The published list says "C" and nothing else. Nobody setting this up knows
//  what C is, and a dropdown of bare letters asks an administrator to guess at
//  something that decides whether a claim gets paid. Every level therefore
//  carries what it MEANS — the kind of facility, and the sort of medicine that
//  sits there — at the point the choice is made, not in a help page.
//
//  ── AND WHY A NUMBER IS NEVER THE END OF A SENTENCE ────────────────────────
//
//  "99 of your medicines are above this facility's level" is a fact nobody can
//  act on. Every count here opens the list behind it: which 99, what they cost,
//  what level each is. The decision — ask NHIA for a higher tier, stop stocking
//  them, or fix a mis-coded product — needs the names in front of it.
//
//  ── UPLOAD IS A PREVIEW, THEN AN APPLY ─────────────────────────────────────
//
//  Applying rewrites the prescribing level of the entire NHIS catalogue. If the
//  file parsed badly, doing that silently would leave every claim misjudged with
//  nothing on screen to say why. So the file is read and REPORTED first.
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import {
  Box, Paper, Typography, Button, Alert, Chip, Switch, Dialog, DialogTitle,
  DialogContent, IconButton, Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, CircularProgress, Divider, Snackbar, LinearProgress, Tooltip,
} from '@mui/material';
import MedicationIcon from '@mui/icons-material/Medication';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ApartmentIcon from '@mui/icons-material/Apartment';
import HistoryIcon from '@mui/icons-material/History';
import CloseIcon from '@mui/icons-material/Close';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DownloadIcon from '@mui/icons-material/Download';
import {
  nhisMedicinesPreview, nhisMedicinesApply, nhisMedicinesVersions,
  nhisMedicinesFacility, nhisMedicinesCoverage, nhisMedicinesProducts,
  nhisMedicinesCodeIssues, nhisMedicinesCodeApply,
  nhisMedicinesDoseUnits, nhisMedicinesDoseApply,
  nhisMedicinesClaimCodes, nhisMedicinesClaimApply,
} from '../BaseURL';

/*  What each kind of code fault means, and whether anything can be done.
 *
 *  Separated because the advice differs completely, and giving one answer to
 *  all three would be wrong twice:
 *
 *    blank         no code; the line cannot be billed. Fixable from the list.
 *    wrong_code    the code belongs to another medicine. Fixable.
 *    name_typo     the CODE IS RIGHT and the name is misspelled. Changing the
 *                  code here would break a working product, so it is reported
 *                  and refused — the name is the facility's own text.
 *    not_on_list   the medicine is not an NHIS medicine but carries a code that
 *                  belongs to one. No right code exists to suggest.
 */
const KINDS = {
  blank: { label: 'No code', tone: 'warn',
    why: 'This medicine has no NHIS code, so the line cannot be billed at all.' },
  wrong_code: { label: 'Wrong code', tone: 'bad',
    why: 'The code on this product belongs to a different medicine. Claims under '
       + 'it are being billed as something else.' },
  name_typo: { label: 'Name misspelled', tone: 'flat',
    why: 'The code is correct — only the medicine\'s name is misspelled here. '
       + 'Nothing to fix in the code; correct the name in the medicines setup.' },
  not_on_list: { label: 'Not an NHIS medicine', tone: 'bad',
    why: 'This medicine is not on the published list, but carries a code that '
       + 'belongs to one that is. It should not be billed to NHIS under this code.' },
};

const PRIMARY = '#1A9B7F';
const PRIMARY_DARK = '#137A64';
const INK = '#0F172A';
const MUTED = '#64748B';
const FAINT = '#94A3B8';
const LINE = '#E7EBF0';
const SOFT = '#F8FAFC';
const AMBER = '#B45309';
const AMBER_BD = '#FDBA74';
const AMBER_BG = '#FFF8EC';
const GREEN = '#15803D';
const GREEN_BD = '#86EFAC';
const GREEN_BG = '#ECFDF3';
const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';
const CARD = {
  borderRadius: 3, border: `1px solid ${LINE}`, background: '#fff',
  boxShadow: '0 1px 2px rgba(15,23,42,.04)',
};

/*  WHAT EACH LEVEL MEANS.
 *
 *  The ladder and the two prescriber types are kept apart here exactly as they
 *  are in the rule that enforces them (nhisMedicinesList.canPrescribe), so the
 *  screen cannot describe a system different from the one running.            */
const TIERS = [
  { v: 'A', title: 'CHPS / community',
    who: 'Community health officer at a CHPS compound',
    eg: 'Albendazole, ORS, activated charcoal' },
  { v: 'B1', title: 'Health centre, nurse-led',
    who: 'Nurse or community health nurse',
    eg: 'Aspirin, benzylpenicillin, aqueous cream' },
  { v: 'B2', title: 'Health centre with a physician assistant',
    who: 'Physician assistant / medical assistant',
    eg: 'Acyclovir, allopurinol, amitriptyline' },
  { v: 'C', title: 'District hospital',
    who: 'Medical officer',
    eg: 'Acetazolamide, acetylcysteine, cefuroxime' },
  { v: 'D', title: 'Regional / teaching hospital',
    who: 'Specialist in a tertiary facility',
    eg: 'Chemotherapy, amino acid infusions' },
];

const EXTRAS = {
  M: { title: 'Midwives\' list', key: 'has_midwife',
    who: 'A midwife, wherever one works',
    eg: 'Ergometrine, magnesium sulphate, folic acid',
    why: 'Not a tier. A health centre with a midwife may prescribe these without being a hospital.' },
  SD: { title: 'Specialist-only', key: 'has_specialist',
    who: 'A resident specialist',
    eg: 'Anastrozole, amiodarone, depot antipsychotics',
    why: 'Not a tier. A district hospital with no resident specialist may not prescribe these.' },
};

const currentUser = () => {
  try { return JSON.parse(localStorage.getItem('userInfo') || '{}')?.name || ''; }
  catch { return ''; }
};

const when = (raw) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(String(raw || ''));
  if (!m) return '';
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${+m[3]} ${MONTHS[+m[2] - 1]} ${m[1]}, ${m[4]}:${m[5]}`;
};

const ghs = (n) => (n == null ? '' : `GH¢ ${Number(n).toFixed(2)}`);

export default function NhisMedicinesList() {
  const me = useRef(currentUser()).current;
  const fileRef = useRef(null);

  const [facility, setFacility] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [versions, setVersions] = useState([]);
  const [preview, setPreview] = useState(null);

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  //  The drill-down: { level } or { only:'above' }
  const [drill, setDrill] = useState(null);
  const [products, setProducts] = useState(null);

  //  Code faults, and which of them the officer has ticked to fix.
  const [issues, setIssues] = useState(null);
  const [picked, setPicked] = useState(() => new Set());
  //  Dose units the published list can supply.
  const [doses, setDoses] = useState(null);
  //  Codes already written onto claims — what the XML export actually sends.
  const [claimCodes, setClaimCodes] = useState(null);

  const load = useCallback(async () => {
    try {
      const [c, v, i, d, cc] = await Promise.all([
        axios.get(nhisMedicinesCoverage),
        axios.get(nhisMedicinesVersions),
        axios.get(nhisMedicinesCodeIssues).catch(() => ({ data: { issues: [] } })),
        axios.get(nhisMedicinesDoseUnits).catch(() => ({ data: { gaps: [] } })),
        axios.get(nhisMedicinesClaimCodes).catch(() => ({ data: { counts: null } })),
      ]);
      setCoverage(c.data);
      setFacility(c.data.facility);
      setVersions(v.data.versions || []);
      setIssues(i.data.issues || []);
      setDoses(d.data || null);
      setClaimCodes(cc.data || null);
      //  Everything with a certain answer starts ticked — the common case is
      //  "apply all of these", and un-ticking a few is less work than ticking
      //  twenty. Nothing without a certain answer is ever pre-selected.
      setPicked(new Set((i.data.issues || [])
        .filter((x) => x.suggested && x.kind !== 'name_typo')
        .map((x) => x.name)));
    } catch (e) {
      setError(e?.response?.data?.message || e.message);
    } finally { setLoading(false); }
  }, []);

  const applyCodes = useCallback(async () => {
    const items = (issues || [])
      .filter((i) => picked.has(i.name) && i.suggested)
      .map((i) => ({ name: i.name, code: i.suggested }));
    if (!items.length) return;
    setBusy('codes'); setError(null);
    try {
      const { data } = await axios.post(nhisMedicinesCodeApply, { items, user: me });
      await load();
      setToast(`${data.applied.length} code${data.applied.length === 1 ? '' : 's'} corrected`
        + `${data.refused.length ? `, ${data.refused.length} refused` : ''}`
        + `${data.relevelled ? ` · ${data.relevelled} products re-levelled` : ''}.`);
    } catch (e) {
      setError(e?.response?.data?.message || e.message);
    } finally { setBusy(null); }
  }, [issues, picked, me, load]);

  useEffect(() => { load(); }, [load]);

  //  Open the list behind a count.
  const openDrill = useCallback(async (q) => {
    setDrill(q); setProducts(null);
    try {
      const { data } = await axios.get(nhisMedicinesProducts, { params: q });
      setProducts(data.products || []);
    } catch (e) {
      setProducts([]);
      setError(e?.response?.data?.message || e.message);
    }
  }, []);

  const saveFacility = useCallback(async (patch) => {
    const next = { ...facility, ...patch };
    setFacility(next);                       // optimistic, so the control moves at once
    setBusy('facility');
    try {
      const { data } = await axios.put(nhisMedicinesFacility, {
        facility_level: next.facility_level || '',
        has_midwife: !!next.has_midwife,
        has_specialist: !!next.has_specialist,
        user: me,
      });
      setFacility(data.facility);
      await load();
    } catch (e) {
      setError(e?.response?.data?.message || e.message);
      await load();                          // put the control back where it was
    } finally { setBusy(null); }
  }, [facility, me, load]);

  const onFile = useCallback(async (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    setBusy('preview'); setError(null); setPreview(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(nhisMedicinesPreview, fd);
      setPreview(data);
    } catch (e) {
      setError(e?.response?.data?.message || e.message);
    } finally {
      setBusy(null);
      if (fileRef.current) fileRef.current.value = '';   // same file can be retried
    }
  }, []);

  const apply = useCallback(async () => {
    if (!preview) return;
    setBusy('apply'); setError(null);
    try {
      const { data } = await axios.post(nhisMedicinesApply, {
        records: preview.records, filename: preview.filename, user: me,
      });
      setPreview(null);
      await load();
      setToast(`${data.stored} medicines stored · ${data.products_levelled} of your `
        + 'products now carry a prescribing level.');
    } catch (e) {
      setError(e?.response?.data?.message || e.message);
    } finally { setBusy(null); }
  }, [preview, me, load]);

  const applyDoses = useCallback(async () => {
    setBusy('doses'); setError(null);
    try {
      //  No items sent: the server fills every gap IT can derive. The browser
      //  never says what value to write — see the route.
      const { data } = await axios.post(nhisMedicinesDoseApply, { user: me });
      await load();
      setToast(`${data.applied.length} dose unit${data.applied.length === 1 ? '' : 's'} `
        + `filled from the published list.`);
    } catch (e) {
      setError(e?.response?.data?.message || e.message);
    } finally { setBusy(null); }
  }, [me, load]);

  const repairClaimCodes = useCallback(async (kinds) => {
    setBusy('claimcodes'); setError(null);
    try {
      const { data } = await axios.post(nhisMedicinesClaimApply, { kinds, user: me });
      await load();
      setToast(`${data.rows} claim line${data.rows === 1 ? '' : 's'} repaired `
        + `across ${data.applied.length} code${data.applied.length === 1 ? '' : 's'}.`);
    } catch (e) {
      setError(e?.response?.data?.message || e.message);
    } finally { setBusy(null); }
  }, [me, load]);

  const exportDrill = useCallback(() => {
    if (!products?.length) return;
    const cell = (v) => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`;
    const csv = [
      ['Code', 'Medicine', 'Level', 'Unit price', 'Status'].map(cell).join(','),
      ...products.map((p) => [p.code, p.name, p.rx_level, p.unit_price,
        p.allowed ? 'allowed' : 'above this level'].map(cell).join(',')),
    ].join('\r\n');
    const url = URL.createObjectURL(new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `nhis-medicines-${drill?.level || 'above-level'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [products, drill]);

  const byLevel = useMemo(() => {
    const m = new Map();
    for (const l of coverage?.levels || []) m.set(l.level, l);
    return m;
  }, [coverage]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', p: 4 }}>
        <CircularProgress size={18} sx={{ color: PRIMARY }} />
        <Typography sx={{ fontSize: 13, color: MUTED }}>Loading…</Typography>
      </Box>
    );
  }

  const cov = coverage?.coverage || { nhis_rows: 0, with_code: 0, levelled: 0 };
  const pct = cov.nhis_rows ? Math.round((cov.levelled / cov.nhis_rows) * 100) : 0;
  const hasLevel = !!facility?.facility_level;
  const above = coverage?.above_level || 0;

  // ── one row of the level ladder ───────────────────────────────────────────
  const TierRow = ({ t }) => {
    const chosen = facility?.facility_level === t.v;
    const info = byLevel.get(t.v);
    const n = info?.n || 0;
    const flagged = hasLevel && info && !info.allowed;
    return (
      <Box component="button" type="button"
        onClick={() => saveFacility({ facility_level: t.v })}
        aria-pressed={chosen}
        sx={{
          width: '100%', textAlign: 'left', font: 'inherit', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5,
          borderRadius: 2.5, position: 'relative',
          border: `1px solid ${chosen ? PRIMARY : LINE}`,
          background: chosen ? `${PRIMARY}0A` : '#fff',
          boxShadow: chosen ? `0 0 0 3px ${PRIMARY}1A` : 'none',
          transition: 'border-color .15s, background .15s, box-shadow .15s',
          '&:hover': { borderColor: chosen ? PRIMARY : '#CBD5E1', background: chosen ? `${PRIMARY}14` : SOFT },
        }}>
        {/*  The letter, sized so the ladder can be read down the left edge. */}
        <Box sx={{
          width: 42, height: 42, flexShrink: 0, borderRadius: 2,
          display: 'grid', placeItems: 'center',
          fontSize: 15, fontWeight: 900, fontFamily: MONO,
          background: chosen ? PRIMARY : SOFT,
          color: chosen ? '#fff' : MUTED,
          border: `1px solid ${chosen ? PRIMARY : LINE}`,
        }}>{t.v}</Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: INK }}>
            {t.title}
          </Typography>
          <Typography sx={{ fontSize: 11.5, color: MUTED, mt: .15 }}>
            {t.who} · e.g. {t.eg}
          </Typography>
        </Box>

        {/*  What this level means for YOUR shelves, right where the choice is
             made — so the consequence is visible before it is chosen. */}
        {n > 0 && (
          <Tooltip title={`${n} of your stocked medicines sit at level ${t.v}`}>
            <Box sx={{
              flexShrink: 0, px: 1, py: .35, borderRadius: 1.5, fontSize: 11, fontWeight: 800,
              background: flagged ? AMBER_BG : GREEN_BG,
              color: flagged ? AMBER : GREEN,
              border: `1px solid ${flagged ? AMBER_BD : GREEN_BD}`,
            }}>{n}</Box>
          </Tooltip>
        )}
        {chosen && (
          <CheckCircleIcon sx={{ fontSize: 19, color: PRIMARY, flexShrink: 0 }} />
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, maxWidth: 1040, mx: 'auto' }}>

      {/* ── header ───────────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, mb: 2.5 }}>
        <Box sx={{
          width: 46, height: 46, borderRadius: 2.5, display: 'grid', placeItems: 'center',
          background: `linear-gradient(140deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)`,
          color: '#fff', boxShadow: `0 4px 12px ${PRIMARY}33`,
        }}><MedicationIcon /></Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 20, fontWeight: 800, color: INK, lineHeight: 1.15 }}>
            NHIS Medicines List
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: MUTED, mt: .2 }}>
            What NHIA will pay this facility to prescribe — checked at the claim
            site, nowhere else
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2.5 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* ── the state of play, in one line of numbers ────────────────────── */}
      <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap', mb: 2 }}>
        {[
          { n: cov.levelled, of: cov.nhis_rows, l: 'medicines levelled',
            tone: 'ok', onClick: () => openDrill({}) },
          { n: above, l: 'above your level', tone: above ? 'warn' : 'ok',
            onClick: above ? () => openDrill({ only: 'above' }) : null },
          { n: cov.nhis_rows - cov.with_code, l: 'have no NHIS code', tone: 'flat' },
          { n: versions.length, l: 'editions held', tone: 'flat' },
        ].map((s, i) => (
          <Box key={i}
            component={s.onClick ? 'button' : 'div'}
            type={s.onClick ? 'button' : undefined}
            onClick={s.onClick || undefined}
            sx={{
              flex: '1 1 170px', textAlign: 'left', font: 'inherit',
              p: 1.75, borderRadius: 2.5, border: `1px solid ${
                s.tone === 'warn' ? AMBER_BD : LINE}`,
              background: s.tone === 'warn' ? AMBER_BG : '#fff',
              cursor: s.onClick ? 'pointer' : 'default',
              transition: 'transform .15s, box-shadow .15s',
              ...(s.onClick && {
                '&:hover': { transform: 'translateY(-2px)',
                  boxShadow: '0 6px 16px rgba(15,23,42,.08)' },
              }),
            }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: .5 }}>
              <Typography sx={{
                fontSize: 26, fontWeight: 800, lineHeight: 1, letterSpacing: -.8,
                fontVariantNumeric: 'tabular-nums',
                color: s.tone === 'warn' ? AMBER : INK,
              }}>{Number(s.n || 0).toLocaleString()}</Typography>
              {s.of != null && (
                <Typography sx={{ fontSize: 13, color: FAINT, fontWeight: 600 }}>
                  / {Number(s.of).toLocaleString()}
                </Typography>
              )}
            </Box>
            <Typography sx={{
              fontSize: 11.5, mt: .5, fontWeight: 600,
              color: s.tone === 'warn' ? AMBER : MUTED,
            }}>
              {s.l}
              {s.onClick && <ChevronRightIcon sx={{ fontSize: 14, verticalAlign: -3 }} />}
            </Typography>
          </Box>
        ))}
      </Box>

      {cov.levelled > 0 && (
        <Box sx={{ mb: 2.5 }}>
          <LinearProgress variant="determinate" value={pct}
            sx={{ height: 6, borderRadius: 99, background: '#EEF2F7',
              '& .MuiLinearProgress-bar': { background: PRIMARY, borderRadius: 99 } }} />
          <Typography sx={{ fontSize: 11, color: FAINT, mt: .75, lineHeight: 1.6 }}>
            {pct}% of your NHIS catalogue carries a level. A product with no level is
            never flagged — it has not been refused, the published list has simply
            never mentioned it.
          </Typography>
        </Box>
      )}

      {/* ── 1. WHAT KIND OF FACILITY IS THIS ─────────────────────────────── */}
      <Paper elevation={0} sx={{ ...CARD, mb: 2, overflow: 'hidden' }}>
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1.5, px: 2.25, py: 1.75,
          borderBottom: `1px solid ${LINE}`, background: SOFT,
        }}>
          <ApartmentIcon sx={{ fontSize: 19, color: MUTED }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: INK }}>
              What kind of facility is this?
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: MUTED }}>
              {facility?.name || 'This facility'} · pick the tier NHIA recognises it as
            </Typography>
          </Box>
          {hasLevel ? (
            <Chip size="small" label={`Level ${facility.facility_level}`}
              sx={{ fontWeight: 800, background: GREEN_BG, color: GREEN,
                border: `1px solid ${GREEN_BD}` }} />
          ) : (
            <Chip size="small" label="Not set — nothing is checked"
              sx={{ fontWeight: 700, background: AMBER_BG, color: AMBER,
                border: `1px solid ${AMBER_BD}` }} />
          )}
        </Box>

        <Box sx={{ p: { xs: 1.5, md: 2.25 }, display: 'grid', gap: 1 }}>
          {TIERS.map((t) => <TierRow key={t.v} t={t} />)}

          {busy === 'facility' && (
            <LinearProgress sx={{ height: 2, borderRadius: 99,
              '& .MuiLinearProgress-bar': { background: PRIMARY } }} />
          )}

          <Divider sx={{ borderColor: LINE, my: .5 }}>
            <Typography sx={{ fontSize: 10.5, fontWeight: 800, color: FAINT,
              textTransform: 'uppercase', letterSpacing: .6 }}>
              Not tiers — who works here
            </Typography>
          </Divider>

          {/*  M and SD are prescriber types. Kept visually apart from the ladder
               because comparing them with it is exactly the mistake to avoid. */}
          {Object.entries(EXTRAS).map(([lvl, x]) => {
            const info = byLevel.get(lvl);
            const on = !!facility?.[x.key];
            return (
              <Box key={lvl} sx={{
                display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5,
                borderRadius: 2.5, border: `1px solid ${LINE}`,
                background: hasLevel ? '#fff' : SOFT, opacity: hasLevel ? 1 : .6,
              }}>
                <Box sx={{
                  width: 42, height: 42, flexShrink: 0, borderRadius: 2,
                  display: 'grid', placeItems: 'center',
                  fontSize: 14, fontWeight: 900, fontFamily: MONO,
                  background: on ? `${PRIMARY}14` : SOFT,
                  color: on ? PRIMARY_DARK : MUTED,
                  border: `1px solid ${on ? PRIMARY : LINE}`,
                }}>{lvl}</Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: INK }}>
                    {x.title}
                  </Typography>
                  <Typography sx={{ fontSize: 11.5, color: MUTED, mt: .15 }}>
                    {x.who} · e.g. {x.eg}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: FAINT, mt: .3 }}>{x.why}</Typography>
                </Box>
                {info?.n > 0 && (
                  <Box sx={{
                    flexShrink: 0, px: 1, py: .35, borderRadius: 1.5,
                    fontSize: 11, fontWeight: 800,
                    background: on ? GREEN_BG : AMBER_BG,
                    color: on ? GREEN : AMBER,
                    border: `1px solid ${on ? GREEN_BD : AMBER_BD}`,
                  }}>{info.n}</Box>
                )}
                <Switch checked={on} disabled={!hasLevel}
                  onChange={(e) => saveFacility({ [x.key]: e.target.checked })}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: PRIMARY },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: PRIMARY },
                  }} />
              </Box>
            );
          })}

          {!hasLevel && (
            <Alert severity="info" sx={{ borderRadius: 2.5, fontSize: 12.5, mt: .5 }}>
              Pick a tier above to switch the cross-check on. Until then nothing is
              flagged anywhere, and the claim site behaves exactly as it does today.
            </Alert>
          )}
        </Box>
      </Paper>

      {/* ── 2. UPLOAD ─────────────────────────────────────────────────────── */}
      <Paper elevation={0} sx={{ ...CARD, mb: 2, overflow: 'hidden' }}>
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1.5, px: 2.25, py: 1.75,
          borderBottom: preview ? `1px solid ${LINE}` : 0, background: SOFT,
        }}>
          <UploadFileIcon sx={{ fontSize: 19, color: MUTED }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: INK }}>
              Upload the published list
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: MUTED }}>
              The NHIA PDF, or a CSV export. You see what will change before anything
              is written.
            </Typography>
          </Box>
          <input ref={fileRef} type="file" accept=".pdf,.csv,.txt"
            onChange={onFile} style={{ display: 'none' }} id="nhis-list-file" />
          <label htmlFor="nhis-list-file">
            <Button component="span" variant="contained" disableElevation
              disabled={busy === 'preview'}
              startIcon={busy === 'preview'
                ? <CircularProgress size={14} sx={{ color: '#fff' }} />
                : <UploadFileIcon />}
              sx={{ background: PRIMARY, fontWeight: 700, borderRadius: 2,
                textTransform: 'none', px: 2, whiteSpace: 'nowrap',
                '&:hover': { background: PRIMARY_DARK } }}>
              {busy === 'preview' ? 'Reading…' : 'Choose a file'}
            </Button>
          </label>
        </Box>

        {preview && (
          <Box sx={{ px: 2.25, py: 2 }}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: INK, mb: 1.25 }}>
              {preview.filename} — read as {preview.format.toUpperCase()}
            </Typography>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
              {[
                { n: preview.parsed.medicines, l: 'medicines in the file' },
                { n: preview.match.stocked_matched, l: 'of your products matched' },
                { n: preview.match.list_not_stocked, l: 'listed, you do not stock' },
                { n: preview.parsed.skipped, l: 'rows skipped' },
              ].map((s, i) => (
                <Box key={i} sx={{
                  flex: '1 1 150px', px: 1.5, py: 1.1, borderRadius: 2,
                  border: `1px solid ${LINE}`, background: SOFT,
                }}>
                  <Typography sx={{ fontSize: 19, fontWeight: 800, color: INK, lineHeight: 1 }}>
                    {Number(s.n || 0).toLocaleString()}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: MUTED, mt: .4 }}>{s.l}</Typography>
                </Box>
              ))}
            </Box>

            <Box sx={{ display: 'flex', gap: .5, flexWrap: 'wrap', mb: 1.5 }}>
              {preview.parsed.levels.map((l) => (
                <Chip key={l.level} size="small" label={`${l.level} · ${l.n}`}
                  sx={{ fontWeight: 700, fontSize: 11, background: SOFT,
                    border: `1px solid ${LINE}` }} />
              ))}
            </Box>

            {preview.match.collisions.length > 0 && (
              <Alert severity="warning" icon={<WarningAmberIcon />}
                sx={{ borderRadius: 2.5, mb: 1.5, background: AMBER_BG, color: AMBER,
                  border: `1px solid ${AMBER_BD}` }}>
                <Typography sx={{ fontSize: 12.5, fontWeight: 800, mb: .75 }}>
                  {preview.match.collisions.length} of your products carry a code that
                  belongs to a different medicine
                </Typography>
                <Typography sx={{ fontSize: 11.5, mb: 1, lineHeight: 1.6 }}>
                  These are already being billed under the wrong code — not caused by
                  this upload, and applying the list will not change them. They need
                  correcting in the medicines setup.
                </Typography>
                <TableContainer sx={{ background: '#fff', borderRadius: 1.5 }}>
                  <Table size="small">
                    <TableBody>
                      {preview.match.collisions.map((c) => (
                        <TableRow key={`${c.code}-${c.trans_code}`}>
                          <TableCell sx={{ fontSize: 11.5, fontWeight: 800, width: 110,
                            fontFamily: MONO }}>{c.code}</TableCell>
                          <TableCell sx={{ fontSize: 11.5 }}>
                            <div>published: <strong>{c.published}</strong></div>
                            <div style={{ color: AMBER }}>you stock: <strong>{c.stocked}</strong></div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <Typography sx={{ fontSize: 11.5, color: FAINT, flex: 1, minWidth: 180 }}>
                Nothing has been saved yet.
              </Typography>
              <Button onClick={() => setPreview(null)}
                sx={{ textTransform: 'none', fontWeight: 600, color: MUTED }}>
                Discard
              </Button>
              <Button variant="contained" disableElevation onClick={apply}
                disabled={busy === 'apply'}
                startIcon={busy === 'apply'
                  ? <CircularProgress size={14} sx={{ color: '#fff' }} />
                  : <CheckCircleIcon />}
                sx={{ background: PRIMARY, fontWeight: 700, borderRadius: 2,
                  textTransform: 'none', px: 2.5 }}>
                {busy === 'apply' ? 'Applying…' : 'Apply this list'}
              </Button>
            </Box>
          </Box>
        )}
      </Paper>

      {/* ── 3. CODE FAULTS ───────────────────────────────────────────────── */}
      {issues?.length > 0 && (() => {
        const fixable = issues.filter((i) => i.suggested && i.kind !== 'name_typo');
        const chosen = fixable.filter((i) => picked.has(i.name)).length;
        return (
          <Paper elevation={0} sx={{ ...CARD, mb: 2, overflow: 'hidden' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5,
              px: 2.25, py: 1.75, borderBottom: `1px solid ${LINE}`, background: SOFT }}>
              <WarningAmberIcon sx={{ fontSize: 19, color: AMBER }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: INK }}>
                  Medicine codes to put right
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: MUTED }}>
                  {issues.length} found · {fixable.length} the published list can
                  settle outright
                </Typography>
              </Box>
              {fixable.length > 0 && (
                <Button variant="contained" disableElevation size="small"
                  onClick={applyCodes} disabled={!chosen || busy === 'codes'}
                  startIcon={busy === 'codes'
                    ? <CircularProgress size={13} sx={{ color: '#fff' }} />
                    : <CheckCircleIcon sx={{ fontSize: 17 }} />}
                  sx={{ background: PRIMARY, fontWeight: 700, borderRadius: 2,
                    textTransform: 'none', whiteSpace: 'nowrap' }}>
                  {busy === 'codes' ? 'Fixing…' : `Fix ${chosen}`}
                </Button>
              )}
            </Box>

            <TableContainer sx={{ maxHeight: 420 }}>
              <Table size="small" stickyHeader>
                <TableBody>
                  {issues.map((i) => {
                    const k = KINDS[i.kind] || KINDS.blank;
                    const canFix = !!i.suggested && i.kind !== 'name_typo';
                    const on = picked.has(i.name);
                    return (
                      <TableRow key={`${i.id}-${i.name}`} hover>
                        <TableCell sx={{ width: 44, pl: 1.5 }}>
                          {canFix ? (
                            <input type="checkbox" checked={on}
                              onChange={() => setPicked((s) => {
                                const n = new Set(s);
                                if (n.has(i.name)) n.delete(i.name); else n.add(i.name);
                                return n;
                              })}
                              style={{ width: 15, height: 15, accentColor: PRIMARY,
                                cursor: 'pointer' }} />
                          ) : (
                            <Tooltip title={k.why}><Box sx={{
                              width: 8, height: 8, borderRadius: '50%', ml: .5,
                              background: k.tone === 'bad' ? '#B91C1C'
                                : k.tone === 'warn' ? AMBER : '#CBD5E1',
                            }} /></Tooltip>
                          )}
                        </TableCell>
                        <TableCell sx={{ py: 1.1 }}>
                          <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: INK }}>
                            {i.name}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: .75, alignItems: 'center',
                            flexWrap: 'wrap', mt: .35 }}>
                            <Chip size="small" label={k.label}
                              sx={{ height: 18, fontSize: 9.5, fontWeight: 800,
                                background: k.tone === 'bad' ? '#FEF2F2'
                                  : k.tone === 'warn' ? AMBER_BG : SOFT,
                                color: k.tone === 'bad' ? '#B91C1C'
                                  : k.tone === 'warn' ? AMBER : MUTED }} />
                            <Typography sx={{ fontSize: 11, color: FAINT, fontFamily: MONO }}>
                              {i.code || 'no code'}
                              {i.published_name && ` = ${String(i.published_name).slice(0, 34)}`}
                            </Typography>
                          </Box>
                          <Typography sx={{ fontSize: 11, color: MUTED, mt: .35, lineHeight: 1.5 }}>
                            {i.message}
                          </Typography>
                          {/*  Candidates are shown but NEVER pre-selected: when
                               two published medicines both fit, the honest
                               answer is to ask. */}
                          {i.candidates?.length > 0 && (
                            <Box sx={{ display: 'flex', gap: .5, flexWrap: 'wrap', mt: .6 }}>
                              {i.candidates.map((c) => (
                                <Tooltip key={c.code} title={c.name}>
                                  <Chip size="small" clickable label={c.code}
                                    onClick={() => {
                                      setIssues((list) => list.map((x) => (x.name === i.name
                                        ? { ...x, suggested: c.code, kind: x.kind } : x)));
                                      setPicked((s) => new Set(s).add(i.name));
                                    }}
                                    sx={{ height: 20, fontSize: 10, fontFamily: MONO,
                                      fontWeight: 700, border: `1px solid ${LINE}` }} />
                                </Tooltip>
                              ))}
                            </Box>
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap', pr: 2 }}>
                          {i.suggested && (
                            <Box sx={{
                              display: 'inline-block', px: 1, py: .4, borderRadius: 1.5,
                              fontSize: 11, fontWeight: 800, fontFamily: MONO,
                              background: GREEN_BG, color: GREEN,
                              border: `1px solid ${GREEN_BD}`,
                            }}>→ {i.suggested}</Box>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ px: 2.25, py: 1.25, borderTop: `1px solid ${LINE}` }}>
              <Typography sx={{ fontSize: 11, color: FAINT, lineHeight: 1.6 }}>
                Only codes the published list actually offers for that medicine can be
                written — the server re-derives every one before it saves. A
                misspelled NAME is reported but never "fixed" here: its code is
                already right, and the name is yours to correct.
              </Typography>
            </Box>
          </Paper>
        );
      })()}

      {/* ── 4. DOSE UNITS ────────────────────────────────────────────────── */}
      {doses?.configured && (doses.gaps?.length > 0 || doses.malformedCount > 0) && (
        <Paper elevation={0} sx={{ ...CARD, mb: 2, overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5,
            px: 2.25, py: 1.75, borderBottom: `1px solid ${LINE}`, background: SOFT }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: INK }}>
                Dose units the list can fill in
              </Typography>
              <Typography sx={{ fontSize: 11.5, color: MUTED }}>
                {doses.missing} of your NHIS medicines have none ·{' '}
                <strong>{doses.gaps.length}</strong> can be read off the published name
              </Typography>
            </Box>
            {doses.gaps.length > 0 && (
              <Button variant="contained" disableElevation size="small"
                onClick={applyDoses} disabled={busy === 'doses'}
                startIcon={busy === 'doses'
                  ? <CircularProgress size={13} sx={{ color: '#fff' }} />
                  : <CheckCircleIcon sx={{ fontSize: 17 }} />}
                sx={{ background: PRIMARY, fontWeight: 700, borderRadius: 2,
                  textTransform: 'none', whiteSpace: 'nowrap' }}>
                {busy === 'doses' ? 'Filling…' : `Fill ${doses.gaps.length}`}
              </Button>
            )}
          </Box>

          {doses.gaps.length > 0 && (
            <TableContainer sx={{ maxHeight: 320 }}>
              <Table size="small" stickyHeader>
                <TableBody>
                  {doses.gaps.map((g) => (
                    <TableRow key={`${g.id}-${g.code}`} hover>
                      <TableCell sx={{ width: 110, fontFamily: MONO, fontSize: 11,
                        color: FAINT, pl: 2 }}>{g.code}</TableCell>
                      <TableCell>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: INK }}>
                          {g.name}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: FAINT }}>
                          from “{g.published_name}”
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ pr: 2, whiteSpace: 'nowrap' }}>
                        <Box sx={{ display: 'inline-block', px: 1, py: .4, borderRadius: 1.5,
                          fontSize: 11.5, fontWeight: 800, fontFamily: MONO,
                          background: GREEN_BG, color: GREEN,
                          border: `1px solid ${GREEN_BD}` }}>{g.dose_unit}</Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/*  Present but not a dose unit — the pricing unit typed into the
               wrong column. Reported, never rewritten: it is somebody's entry
               and replacing it is a different act from filling a blank. */}
          {doses.malformedCount > 0 && (
            <Box sx={{ px: 2.25, py: 1.5, borderTop: `1px solid ${LINE}`,
              background: AMBER_BG }}>
              <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: AMBER, mb: .5 }}>
                {doses.malformedCount} medicine{doses.malformedCount === 1 ? '' : 's'} hold
                something that is not a dose unit
              </Typography>
              <Typography sx={{ fontSize: 11, color: AMBER, lineHeight: 1.6, mb: .75 }}>
                Values like “Vial” or “1” — the pricing unit in the dose column. These are
                <strong> not</strong> changed here, because overwriting an entry somebody
                made is a different decision from filling a blank. Correct them in the
                medicines setup.
              </Typography>
              <Box sx={{ display: 'flex', gap: .5, flexWrap: 'wrap' }}>
                {doses.malformed.slice(0, 10).map((m) => (
                  <Tooltip key={m.id} title={m.name}>
                    <Chip size="small" label={`${m.value}`}
                      sx={{ height: 20, fontSize: 10, fontFamily: MONO, background: '#fff',
                        border: `1px solid ${AMBER_BD}`, color: AMBER }} />
                  </Tooltip>
                ))}
              </Box>
            </Box>
          )}

          <Box sx={{ px: 2.25, py: 1.25, borderTop: `1px solid ${LINE}` }}>
            <Typography sx={{ fontSize: 11, color: FAINT, lineHeight: 1.6 }}>
              Matched on the CODE and read off the published name — “Diclofenac Tablet,
              50 mg” states the strength where your own “DICLO TAB 50MG” may not. Only
              EMPTY dose units are filled, so this is safe to run after every upload.
            </Typography>
          </Box>
        </Paper>
      )}

      {/* ── 5. THE CODES ALREADY ON CLAIMS ───────────────────────────────── */}
      {claimCodes?.counts && (claimCodes.counts.trim + claimCodes.counts.recode
        + claimCodes.counts.unbillable) > 0 && (
        <Paper elevation={0} sx={{ ...CARD, mb: 2, overflow: 'hidden' }}>
          <Box sx={{ px: 2.25, py: 1.75, borderBottom: `1px solid ${LINE}`, background: SOFT }}>
            <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: INK }}>
              Codes already on claims
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: MUTED }}>
              This is what the XML export sends as <code>&lt;medicineCode&gt;</code> —
              fixing the catalogue does not change claims already written
            </Typography>
          </Box>

          <Box sx={{ px: 2.25, py: 2, display: 'grid', gap: 1.5 }}>
            {/*  RIGHT code, dirty. No judgement needed — the published list
                 already holds the normalised form. */}
            {claimCodes.counts.trim > 0 && (
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap',
                p: 1.5, borderRadius: 2.5, border: `1px solid ${GREEN_BD}`, background: GREEN_BG }}>
                <Box sx={{ flex: 1, minWidth: 220 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 800, color: GREEN }}>
                    {claimCodes.counts.trim} lines — right code, stray space
                  </Typography>
                  <Typography sx={{ fontSize: 11.5, color: GREEN, mt: .25, lineHeight: 1.5 }}>
                    NHIA reads “DICLOFIN 1”, finds no such medicine and rejects the
                    claim over a code that was correct. Safe to apply — the published
                    list already holds the cleaned form.
                  </Typography>
                  <Box sx={{ display: 'flex', gap: .5, flexWrap: 'wrap', mt: .75 }}>
                    {claimCodes.trim.slice(0, 6).map((t, i) => (
                      <Chip key={i} size="small"
                        label={`${t.n}× ${t.code.trim()} → ${t.to}`}
                        sx={{ height: 20, fontSize: 10, fontFamily: MONO, background: '#fff',
                          border: `1px solid ${GREEN_BD}`, color: GREEN }} />
                    ))}
                  </Box>
                </Box>
                <Button variant="contained" disableElevation size="small"
                  onClick={() => repairClaimCodes(['trim'])} disabled={busy === 'claimcodes'}
                  startIcon={busy === 'claimcodes'
                    ? <CircularProgress size={13} sx={{ color: '#fff' }} />
                    : <CheckCircleIcon sx={{ fontSize: 17 }} />}
                  sx={{ background: PRIMARY, fontWeight: 700, borderRadius: 2,
                    textTransform: 'none', whiteSpace: 'nowrap' }}>
                  {busy === 'claimcodes' ? 'Repairing…' : `Clean ${claimCodes.counts.trim}`}
                </Button>
              </Box>
            )}

            {/*  The catalogue has since been corrected and the claim has not. */}
            {claimCodes.counts.recode > 0 && (
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap',
                p: 1.5, borderRadius: 2.5, border: `1px solid ${LINE}` }}>
                <Box sx={{ flex: 1, minWidth: 220 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 800, color: INK }}>
                    {claimCodes.counts.recode} lines — the catalogue now says otherwise
                  </Typography>
                  <Typography sx={{ fontSize: 11.5, color: MUTED, mt: .25, lineHeight: 1.5 }}>
                    These carry a code the published list does not know, and your own
                    medicines list now holds a published one for the same medicine.
                  </Typography>
                </Box>
                <Button variant="outlined" size="small"
                  onClick={() => repairClaimCodes(['recode'])} disabled={busy === 'claimcodes'}
                  sx={{ fontWeight: 700, borderRadius: 2, textTransform: 'none',
                    color: PRIMARY_DARK, borderColor: PRIMARY, whiteSpace: 'nowrap' }}>
                  Re-point {claimCodes.counts.recode}
                </Button>
              </Box>
            )}

            {/*  No right answer exists, so nothing is offered. */}
            {claimCodes.counts.unbillable > 0 && (
              <Box sx={{ p: 1.5, borderRadius: 2.5, border: `1px solid ${AMBER_BD}`,
                background: AMBER_BG }}>
                <Typography sx={{ fontSize: 13, fontWeight: 800, color: AMBER }}>
                  {claimCodes.counts.unbillable} lines — no match on the published list
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: AMBER, mt: .25, lineHeight: 1.6 }}>
                  Across {claimCodes.counts.unbillableCodes} codes. Nothing is changed:
                  there is no right code to write, and inventing one would bill a
                  medicine NHIA does not cover under the name of one it does. Check
                  these against the full NHIA list — the edition uploaded here may not
                  carry every medicine you dispense.
                </Typography>
                <Box sx={{ display: 'flex', gap: .5, flexWrap: 'wrap', mt: .75 }}>
                  {claimCodes.unbillable.slice(0, 12).map((u, i) => (
                    <Tooltip key={i} title={u.name || ''}>
                      <Chip size="small" label={`${u.n}× ${u.code}`}
                        sx={{ height: 20, fontSize: 10, fontFamily: MONO, background: '#fff',
                          border: `1px solid ${AMBER_BD}`, color: AMBER }} />
                    </Tooltip>
                  ))}
                </Box>
              </Box>
            )}
          </Box>

          <Box sx={{ px: 2.25, py: 1.25, borderTop: `1px solid ${LINE}` }}>
            <Typography sx={{ fontSize: 11, color: FAINT, lineHeight: 1.6 }}>
              The exporter also strips spaces from the code on its way out, so a dirty
              value can never reach NHIA again even before this repair is run.
            </Typography>
          </Box>
        </Paper>
      )}

      {/* ── 6. EDITIONS ──────────────────────────────────────────────────── */}
      {versions.length > 0 && (
        <Paper elevation={0} sx={{ ...CARD, overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5,
            px: 2.25, py: 1.75, borderBottom: `1px solid ${LINE}`, background: SOFT }}>
            <HistoryIcon sx={{ fontSize: 19, color: MUTED }} />
            <Box>
              <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: INK }}>
                Editions held
              </Typography>
              <Typography sx={{ fontSize: 11.5, color: MUTED }}>
                Older lists are kept, so a claim billed months ago can still be explained
              </Typography>
            </Box>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Edition', 'Medicines', 'Uploaded', 'By', ''].map((h, i) => (
                    <TableCell key={i} sx={{ fontSize: 10.5, fontWeight: 700, color: MUTED,
                      textTransform: 'uppercase', letterSpacing: .5, background: '#FBFCFD' }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {versions.map((v) => (
                  <TableRow key={v.id} hover>
                    <TableCell sx={{ fontSize: 12.5, fontWeight: 700, color: INK }}>
                      {v.label}
                      {v.filename && (
                        <Typography sx={{ fontSize: 11, color: FAINT }}>{v.filename}</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12.5, color: MUTED }}>{v.medicines}</TableCell>
                    <TableCell sx={{ fontSize: 12, color: MUTED, whiteSpace: 'nowrap' }}>
                      {when(v.uploaded_at)}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12, color: MUTED }}>{v.uploaded_by || '—'}</TableCell>
                    <TableCell>
                      {Number(v.is_active) === 1 && (
                        <Chip size="small" label="in force"
                          sx={{ fontSize: 10, height: 20, fontWeight: 700,
                            background: GREEN_BG, color: GREEN }} />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* ── the list behind a number ─────────────────────────────────────── */}
      <Dialog open={!!drill} onClose={() => setDrill(null)} fullWidth maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 15.5, fontWeight: 800, color: INK }}>
              {drill?.only === 'above' ? 'Above this facility\'s level'
                : drill?.level ? `Level ${drill.level}` : 'Every levelled medicine'}
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: MUTED }}>
              {products == null ? 'Reading…' : `${products.length} medicine${products.length === 1 ? '' : 's'}`}
              {drill?.only === 'above'
                && ' · these can still be prescribed, the claim carries a warning'}
            </Typography>
          </Box>
          {products?.length > 0 && (
            <Button size="small" startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
              onClick={exportDrill}
              sx={{ textTransform: 'none', fontWeight: 600, color: MUTED }}>
              Export
            </Button>
          )}
          <IconButton size="small" onClick={() => setDrill(null)}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {products == null ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <CircularProgress size={20} sx={{ color: PRIMARY }} />
            </Box>
          ) : products.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography sx={{ fontSize: 13, color: MUTED }}>Nothing here.</Typography>
            </Box>
          ) : (
            <TableContainer sx={{ maxHeight: '58vh' }}>
              <Table size="small" stickyHeader>
                <TableBody>
                  {products.map((p) => (
                    <TableRow key={p.id} hover>
                      <TableCell sx={{ width: 56 }}>
                        <Box sx={{
                          width: 30, height: 24, borderRadius: 1.2, display: 'grid',
                          placeItems: 'center', fontSize: 11, fontWeight: 900, fontFamily: MONO,
                          background: p.allowed ? GREEN_BG : AMBER_BG,
                          color: p.allowed ? GREEN : AMBER,
                          border: `1px solid ${p.allowed ? GREEN_BD : AMBER_BD}`,
                        }}>{p.rx_level}</Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: 12.5 }}>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: INK }}>
                          {p.name}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: FAINT, fontFamily: MONO }}>
                          {p.code || 'no code'}
                          {p.unit_price != null && ` · ${ghs(p.unit_price)}`}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: 11, color: AMBER, maxWidth: 190 }}>
                        {p.allowed ? '' : p.note}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={5000} onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="success" variant="filled" onClose={() => setToast(null)}
          sx={{ borderRadius: 2, fontWeight: 600 }}>{toast}</Alert>
      </Snackbar>
    </Box>
  );
}
